import { useRef, useState, type ReactElement, type RefObject } from "react";
import { useLocation } from "react-router";

import type { FieldErrors } from "../../api/client.js";
import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";

import { CenterBackLink } from "./center-back-link.js";
import { ChargeImportSection } from "./charge-import.js";
import { PausedSubscriptions } from "./charge-paused.js";
import { ChargeRows } from "./charge-rows.js";
import { ChargeSearchForm } from "./charge-search-form.js";
import type { CenterCharges } from "./charge-types.js";
import { CsvDownload, csvLimitLabel } from "./csv-download.js";
import { Pager } from "./pager.js";
import { conditionsOf } from "./payment-page.js";
import type { PaymentResultChoice } from "./payment-search-form.js";
import { PaymentTabs } from "./payment-tabs.js";
import { adminCenterApi, adminCenterUrls } from "./urls.js";

const noErrors: FieldErrors = {};

/** 契約で絞っていることを出す（契約の名前はサーバが返す。無い契約ならその旨）。 */
function TenantScope({ found }: { readonly found: CenterCharges }): ReactElement | null {
    if (found.tenantId === null) {
        return null;
    }
    const id = String(found.tenantId);
    return (
        <p className="mb-3 text-sm text-slate-500" data-charge-tenant="">
            {found.tenantTitle === null
                ? `契約 ID ${id} に絞っています（この番号の契約は見つかりません）。`
                : `契約「${found.tenantTitle}」（契約 ID ${id}）に絞っています。`}
        </p>
    );
}

function Results({ found, top }: {
    readonly found: CenterCharges;
    readonly top: RefObject<HTMLDivElement | null>;
}): ReactElement {
    if (found.rows.length === 0) {
        return <div className="empty-state text-sm text-slate-500">当たる課金がありません。</div>;
    }
    const limit = csvLimitLabel(found.csvLimit);

    return (
        <>
            <CsvDownload
                filePrefix="payjp_charges"
                url={adminCenterApi.chargesCsv({
                    from: found.from, to: found.to, result: found.result, keyword: found.keyword, tenantId: found.tenantId,
                })}
                summary={`${String(found.matchingCount)} 件`}
                help={`CSV には、ページに関係なく当たる課金を新しい順に ${limit} 件まで書き出します。`}
                warning={found.matchingCount > found.csvLimit
                    ? `当たりが ${limit} 件を超えています。CSV には新しい順に ${limit} 件までを書き出します。期間で絞ってください。`
                    : null}
            />
            <div className="table-card" ref={top}>
                <div className="table-scroll">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th scope="col">日時</th>
                                <th scope="col">契約</th>
                                <th scope="col">金額</th>
                                <th scope="col">結果</th>
                                <th scope="col">失敗の理由</th>
                                <th scope="col">PAY.JP の ID</th>
                            </tr>
                        </thead>
                        <ChargeRows rows={found.rows} />
                    </table>
                </div>
            </div>
            <Pager
                page={found.page}
                pageCount={found.pageCount}
                hasNext={found.page < found.pageCount}
                total={`全 ${String(found.matchingCount)} 件`}
                hrefOf={(page) => adminCenterUrls.charges({
                    from: found.from, to: found.to, result: found.result, keyword: found.keyword, tenantId: found.tenantId, page,
                })}
                top={top}
                marker={{ "data-charge-pager": "" }}
            />
        </>
    );
}

/**
 * PAY.JP の課金（毎月の定期課金の請求の結果。課金の結果の 1 つ目のタブ）。
 * <b>PAY.JP から取り込んだ記録を並べるだけ</b>——開いても PAY.JP は呼ばない。取り込みは自動（常駐処理）と「今すぐ取り込む」。
 * <b>条件は URL のクエリ</b>（<c>from</c>・<c>to</c>・<c>result</c>・<c>keyword</c>・<c>tenantId</c>・<c>page</c>）。
 * <c>tenantId</c> は契約 1 件の画面の入口から渡ってくる（その契約に絞る）。開けるかはサーバが決める（請求の権限。無ければ 404）。
 *
 * <b>期間・結果の誤りは 400（ValidationProblem）で断られる。</b>そのときも窓とタブは残し、断りを欄の下（期間以外は先頭）に出す
 * （課金の結果の画面と同じ）。取り込みと停止中の区画は、届いたときだけ出す。
 */
export function ChargePage(): ReactElement | null {
    const { search } = useLocation();
    const data = useApiData<CenterCharges>(adminCenterApi.charges(search));
    usePageMeta(titleFor(data, "課金の結果"));
    const top = useRef<HTMLDivElement | null>(null);
    const [results, setResults] = useState<readonly PaymentResultChoice[]>([]);
    if (data.state === "ready" && data.value.results !== results) {
        setResults(data.value.results);
    }

    const rejected = data.state === "failed" && data.status === 400 && data.errors !== undefined;
    if (data.state !== "ready" && !rejected) {
        return <ApiState data={data} what="PAY.JP の課金" />;
    }

    return (
        <div className="page max-w-6xl">
            <CenterBackLink />
            <div className="page-header">
                <div>
                    <h1 className="page-title">課金の結果</h1>
                    <p className="page-subtitle">
                        PAY.JP が毎月の定期課金で請求した結果（成功・失敗・返金）を、PAY.JP から取り込んだ記録で探します。
                        開いても PAY.JP は呼びません。
                    </p>
                </div>
            </div>
            <PaymentTabs current="charges" />

            {/* 窓は断られたときも同じ位置に描く（作り直すとフォーカスが外れる。docs/01 §8）。断られて消える区画は窓より下に置く */}
            {data.state === "ready"
                ? <ChargeSearchForm conditions={data.value} results={data.value.results} errors={noErrors} />
                : <ChargeSearchForm conditions={conditionsOf(search)} results={results} errors={data.errors ?? noErrors} />}
            {data.state === "ready"
                ? <PausedSubscriptions rows={data.value.pausedSubscriptions} count={data.value.pausedCount} />
                : null}
            {data.state === "ready" ? <TenantScope found={data.value} /> : null}
            {data.state === "ready" ? <Results found={data.value} top={top} /> : null}
            {data.state === "ready" ? <ChargeImportSection state={data.value.import} /> : null}
        </div>
    );
}
